import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from './useOrganization';
import { toZonedTime, format as formatTz } from 'date-fns-tz';

// Track IDs that the current user acted on to suppress self-notifications
const selfActionedIds = new Set<string>();
const SELF_ACTION_EXPIRY_MS = 30_000; // 30 seconds — generous window for slow realtime delivery

export function registerSelfAction(id: string) {
  selfActionedIds.add(id);
  setTimeout(() => selfActionedIds.delete(id), SELF_ACTION_EXPIRY_MS);
}

export interface Notification {
  id: string;
  type: 'quote_view' | 'invoice_view' | 'quote_approved' | 'invoice_paid' | 'appointment_booked' | 'appointment_cancelled' | 'appointment_rescheduled' | 'quote_reminder' | 'invoice_reminder' | 'ticket_reply' | 'client_message';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  relatedId?: string;
}

// Map notification types to SMS event types
const notificationTypeToSMSEvent: Record<string, string> = {
  quote_view: 'quote_viewed',
  invoice_view: 'invoice_viewed',
  quote_approved: 'quote_approved',
  invoice_paid: 'invoice_paid',
  appointment_booked: 'appointment_booked',
  appointment_cancelled: 'appointment_cancelled',
  client_message: 'message_received',
};

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { organization } = useOrganization();

  // Helper to persist a notification to the database
  const persistNotification = useCallback(async (notification: Notification) => {
    if (!organization?.id) return;
    try {
      await supabase.from('notifications').insert({
        id: notification.id.length <= 36 ? undefined : undefined, // let DB generate
        organization_id: organization.id,
        type: notification.type,
        title: notification.title,
        message: notification.message,
        read: false,
        related_id: notification.relatedId || null,
      });
    } catch (error) {
      console.log('Failed to persist notification:', error);
    }
  }, [organization?.id]);

  // Helper to send SMS notification
  const sendSMSNotification = useCallback(async (eventType: string, message: string) => {
    if (!organization?.id) return;
    
    try {
      await supabase.functions.invoke('send-sms-notification', {
        body: {
          organizationId: organization.id,
          eventType,
          message,
        },
      });
    } catch (error) {
      console.log('SMS notification skipped:', error);
    }
  }, [organization?.id]);

  // Helper to send email notification to business
  const sendEmailNotification = useCallback(async (
    eventType: string,
    subject: string,
    message: string,
    details?: {
      clientName?: string;
      clientEmail?: string;
      amount?: number;
      documentNumber?: string;
      documentId?: string;
      appointmentDate?: string;
    }
  ) => {
    if (!organization?.id) return;
    
    try {
      await supabase.functions.invoke('send-business-notification-email', {
        body: {
          organizationId: organization.id,
          eventType,
          subject,
          message,
          details,
        },
      });
    } catch (error) {
      console.log('Email notification skipped:', error);
    }
  }, [organization?.id]);

  // Persist notification to DB — the realtime subscription will handle adding it to state
  const addNotification = useCallback((notification: Notification) => {
    persistNotification(notification);
  }, [persistNotification]);

  // Load existing notifications from the database on mount
  useEffect(() => {
    if (!organization?.id) return;

    const loadNotifications = async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (data && !error) {
        const mapped: Notification[] = data.map((n: any) => ({
          id: n.id,
          type: n.type as Notification['type'],
          title: n.title,
          message: n.message,
          timestamp: n.created_at,
          read: n.read,
          relatedId: n.related_id || undefined,
        }));
        setNotifications(mapped);
        setUnreadCount(mapped.filter((n) => !n.read).length);
      }
    };

    loadNotifications();
  }, [organization?.id]);

  useEffect(() => {
    if (!organization?.id) return;

    // Subscribe to new notifications in the DB (covers quote/invoice views persisted by edge function)
    const notificationsChannel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${organization.id}`,
        },
        (payload) => {
          const n = payload.new as any;
          setNotifications((prev) => {
            if (prev.some((existing) => existing.id === n.id)) return prev;
            const mapped: Notification = {
              id: n.id,
              type: n.type as Notification['type'],
              title: n.title,
              message: n.message,
              timestamp: n.created_at,
              read: n.read,
              relatedId: n.related_id || undefined,
            };
            return [mapped, ...prev];
          });
          setUnreadCount((prev) => prev + 1);
        }
      )
      .subscribe();

    // Subscribe to quote approvals
    const quotesChannel = supabase
      .channel('quotes-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'quotes',
          filter: `organization_id=eq.${organization.id}`,
        },
        (payload) => {
          if (payload.new.status === 'APPROVED' && payload.old.status !== 'APPROVED') {
            // Skip notification if the current user performed this action
            if (selfActionedIds.has(payload.new.id)) {
              selfActionedIds.delete(payload.new.id);
              return;
            }
            const notificationMessage = `Quote #${payload.new.number} was approved by ${payload.new.approved_by_name}`;
            const newNotification: Notification = {
              id: crypto.randomUUID(),
              type: 'quote_approved',
              title: 'Quote Approved',
              message: notificationMessage,
              timestamp: new Date().toISOString(),
              read: false,
              relatedId: payload.new.id,
            };
            addNotification(newNotification);
            
            sendSMSNotification('quote_approved', notificationMessage);
            sendEmailNotification('quote_approved', '🎉 Quote Approved!', notificationMessage, {
              clientName: payload.new.approved_by_name,
              clientEmail: payload.new.approved_by_email,
              documentNumber: String(payload.new.number),
              documentId: payload.new.id,
              amount: payload.new.total,
            });
          }
          
          if (payload.new.status === 'DEPOSIT_PAID' && payload.old.status !== 'DEPOSIT_PAID') {
            // Skip notification if the current user performed this action
            if (selfActionedIds.has(payload.new.id)) {
              selfActionedIds.delete(payload.new.id);
              return;
            }
            const hadDepositDue = payload.new.deposit_due > 0;
            
            if (hadDepositDue) {
              const notificationMessage = `Deposit received for Quote #${payload.new.number}`;
              const newNotification: Notification = {
                id: crypto.randomUUID(),
                type: 'quote_approved',
                title: 'Deposit Paid',
                message: notificationMessage,
                timestamp: new Date().toISOString(),
                read: false,
                relatedId: payload.new.id,
              };
              addNotification(newNotification);
              
              sendSMSNotification('deposit_paid', notificationMessage);
              sendEmailNotification('deposit_paid', '💰 Deposit Received!', notificationMessage, {
                documentNumber: String(payload.new.number),
                documentId: payload.new.id,
                amount: payload.new.deposit_due,
              });
            }
          }
        }
      )
      .subscribe();

    // Subscribe to invoice payments
    const invoicesChannel = supabase
      .channel('invoices-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'invoices',
          filter: `organization_id=eq.${organization.id}`,
        },
        (payload) => {
          if (payload.new.status === 'PAID' && payload.old.status !== 'PAID') {
            // Skip notification if the current user performed this action
            if (selfActionedIds.has(payload.new.id)) {
              selfActionedIds.delete(payload.new.id);
              return;
            }
            const notificationMessage = `Invoice #${payload.new.number} has been paid`;
            const newNotification: Notification = {
              id: crypto.randomUUID(),
              type: 'invoice_paid',
              title: 'Invoice Paid',
              message: notificationMessage,
              timestamp: new Date().toISOString(),
              read: false,
              relatedId: payload.new.id,
            };
            addNotification(newNotification);
            
            sendSMSNotification('invoice_paid', notificationMessage);
            sendEmailNotification('invoice_paid', '💰 Payment Received!', notificationMessage, {
              documentNumber: String(payload.new.number),
              documentId: payload.new.id,
              amount: payload.new.total,
            });
          }
        }
      )
      .subscribe();

    // Subscribe to new appointments
    const appointmentsChannel = supabase
      .channel('appointments-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'appointments',
          filter: `organization_id=eq.${organization.id}`,
        },
        async (payload) => {
          const { data: bookingSettings } = await supabase
            .from('booking_settings')
            .select('timezone')
            .eq('organization_id', organization.id)
            .single();
          
          const timezone = bookingSettings?.timezone || 'America/New_York';
          const appointmentDate = new Date(payload.new.start_at);
          const zonedDate = toZonedTime(appointmentDate, timezone);
          
          const formattedDate = formatTz(zonedDate, 'EEEE, MMMM d, yyyy', { timeZone: timezone });
          const formattedTime = formatTz(zonedDate, 'h:mm a', { timeZone: timezone });
          const formattedDateTime = `${formattedDate} at ${formattedTime}`;
          
          const notificationMessage = `${payload.new.name} booked an appointment for ${formattedDate}`;
          // Dashboard notification is now persisted server-side via DB trigger (notify_on_appointment_booked)
          // and picked up by the notifications-realtime channel — no need for addNotification here.
          sendSMSNotification('appointment_booked', notificationMessage);
          sendEmailNotification('appointment_booked', '📅 New Appointment Booked!', notificationMessage, {
            clientName: payload.new.name,
            clientEmail: payload.new.email,
            documentId: payload.new.id,
            appointmentDate: formattedDateTime,
          });
        }
      )
      .subscribe();

    // Subscribe to appointment updates (cancellations and reschedules)
    const appointmentUpdatesChannel = supabase
      .channel('appointment-updates-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'appointments',
          filter: `organization_id=eq.${organization.id}`,
        },
        async (payload) => {
          const { data: bookingSettings } = await supabase
            .from('booking_settings')
            .select('timezone')
            .eq('organization_id', organization.id)
            .single();
          
          const timezone = bookingSettings?.timezone || 'America/New_York';
          const appointmentDate = new Date(payload.new.start_at);
          const zonedDate = toZonedTime(appointmentDate, timezone);
          
          const formattedDate = formatTz(zonedDate, 'EEEE, MMMM d, yyyy', { timeZone: timezone });
          const formattedTime = formatTz(zonedDate, 'h:mm a', { timeZone: timezone });
          const formattedDateTime = `${formattedDate} at ${formattedTime}`;
          
          if (payload.new.status === 'CANCELLED' && payload.old.status !== 'CANCELLED') {
            // Skip notifications if the user themselves cancelled (not client)
            if (payload.new.cancelled_by === 'user') {
              console.log('Skipping cancel notification — user cancelled their own appointment');
              return;
            }
            const notificationMessage = `${payload.new.name} cancelled their appointment for ${formattedDate}`;
            const newNotification: Notification = {
              id: crypto.randomUUID(),
              type: 'appointment_cancelled',
              title: 'Appointment Cancelled',
              message: notificationMessage,
              timestamp: new Date().toISOString(),
              read: false,
              relatedId: payload.new.id,
            };
            addNotification(newNotification);
            sendSMSNotification('appointment_cancelled', notificationMessage);
            sendEmailNotification('appointment_cancelled', '❌ Appointment Cancelled', notificationMessage, {
              clientName: payload.new.name,
              clientEmail: payload.new.email,
              documentId: payload.new.id,
              appointmentDate: formattedDateTime,
            });
          }
          else if (
            payload.new.status === 'SCHEDULED' &&
            payload.old.status === 'SCHEDULED' &&
            (payload.new.start_at !== payload.old.start_at || payload.new.end_at !== payload.old.end_at)
          ) {
            const newNotification: Notification = {
              id: crypto.randomUUID(),
              type: 'appointment_rescheduled',
              title: 'Appointment Rescheduled',
              message: `${payload.new.name} rescheduled their appointment to ${formattedDateTime}`,
              timestamp: new Date().toISOString(),
              read: false,
              relatedId: payload.new.id,
            };
            addNotification(newNotification);
          }
        }
      )
      .subscribe();

    // Subscribe to reminder notifications
    const remindersChannel = supabase
      .channel('reminders-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'reminder_history',
          filter: `organization_id=eq.${organization.id}`,
        },
        async (payload) => {
          let notificationData: Notification | null = null;

          if (payload.new.quote_id) {
            const { data: quote } = await supabase
              .from('quotes')
              .select('number, title, clients(name)')
              .eq('id', payload.new.quote_id)
              .single();

            if (quote) {
              notificationData = {
                id: crypto.randomUUID(),
                type: 'quote_reminder',
                title: 'Quote Reminder Sent',
                message: `Reminder #${payload.new.reminder_number} sent for Quote #${quote.number} to ${quote.clients?.name}`,
                timestamp: new Date().toISOString(),
                read: false,
                relatedId: payload.new.quote_id,
              };
            }
          } else if (payload.new.invoice_id) {
            const { data: invoice } = await supabase
              .from('invoices')
              .select('number, clients(name)')
              .eq('id', payload.new.invoice_id)
              .single();

            if (invoice) {
              notificationData = {
                id: crypto.randomUUID(),
                type: 'invoice_reminder',
                title: 'Invoice Reminder Sent',
                message: `Reminder #${payload.new.reminder_number} sent for Invoice #${invoice.number} to ${invoice.clients?.name}`,
                timestamp: new Date().toISOString(),
                read: false,
                relatedId: payload.new.invoice_id,
              };
            }
          }

          if (notificationData) {
            addNotification(notificationData);
          }
        }
      )
      .subscribe();

    // Subscribe to admin replies on support tickets
    const ticketRepliesChannel = supabase
      .channel('ticket-replies-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'support_ticket_replies',
        },
        async (payload) => {
          if (!payload.new.is_admin_reply) return;

          const { data: ticket } = await supabase
            .from('support_tickets')
            .select('subject, organization_id')
            .eq('id', payload.new.ticket_id)
            .single();

          if (ticket && ticket.organization_id === organization.id) {
            const newNotification: Notification = {
              id: crypto.randomUUID(),
              type: 'ticket_reply',
              title: 'Support Reply Received',
              message: `New reply on your ticket: "${ticket.subject}"`,
              timestamp: new Date().toISOString(),
              read: false,
              relatedId: payload.new.ticket_id,
            };
            addNotification(newNotification);
          }
        }
      )
      .subscribe();

    // Subscribe to new client messages
    const clientMessagesChannel = supabase
      .channel('client-messages-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'client_messages',
          filter: `organization_id=eq.${organization.id}`,
        },
        async (payload) => {
          if (payload.new.sender_type !== 'client') return;

          const { data: client } = await supabase
            .from('clients')
            .select('name')
            .eq('id', payload.new.client_id)
            .single();

          if (client) {
            const newNotification: Notification = {
              id: crypto.randomUUID(),
              type: 'client_message',
              title: 'New Message',
              message: `${client.name} sent you a message`,
              timestamp: new Date().toISOString(),
              read: false,
              relatedId: payload.new.client_id,
            };
            addNotification(newNotification);
            
            sendEmailNotification('client_message', '💬 New Client Message', `${client.name} sent you a message`, {
              clientName: client.name,
              documentId: payload.new.client_id,
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(notificationsChannel);
      supabase.removeChannel(quotesChannel);
      supabase.removeChannel(invoicesChannel);
      supabase.removeChannel(appointmentsChannel);
      supabase.removeChannel(appointmentUpdatesChannel);
      supabase.removeChannel(remindersChannel);
      supabase.removeChannel(ticketRepliesChannel);
      supabase.removeChannel(clientMessagesChannel);
    };
  }, [organization?.id, sendSMSNotification, sendEmailNotification, addNotification]);

  const markAsRead = useCallback(async (notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
    
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId);
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!organization?.id) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('organization_id', organization.id)
      .eq('read', false);
  }, [organization?.id]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    setNotifications((prev) => {
      const n = prev.find((x) => x.id === notificationId);
      if (n && !n.read) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      return prev.filter((x) => x.id !== notificationId);
    });
    
    await supabase.from('notifications').delete().eq('id', notificationId);
  }, []);

  const clearAll = useCallback(async () => {
    if (!organization?.id) return;
    setNotifications([]);
    setUnreadCount(0);
    
    await supabase
      .from('notifications')
      .delete()
      .eq('organization_id', organization.id);
  }, [organization?.id]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  };
};
